import type { LoremCorpus, LoremSourceLoader, LoremSourceName } from "./types";

// Lazy-loading source registry per Engineering brief §C6.
// Each source registers a loader that returns the corpus on first
// `loadSource` call; subsequent calls reuse the cached promise.

const loaders = new Map<LoremSourceName, LoremSourceLoader>();
const cache = new Map<LoremSourceName, Promise<LoremCorpus>>();

/**
 * Register a source by name + lazy loader. Idempotent: re-registering
 * replaces the loader and clears the cache.
 */
export function registerSource(
	name: LoremSourceName,
	loader: LoremSourceLoader,
): void {
	loaders.set(name, loader);
	cache.delete(name);
}

/**
 * Load a registered source corpus. Throws if the source isn't
 * registered. Caches the promise so the network/dynamic-import only
 * fires once per source.
 */
export function loadSource(name: LoremSourceName): Promise<LoremCorpus> {
	const cached = cache.get(name);
	if (cached) return cached;
	const loader = loaders.get(name);
	if (!loader) {
		throw new Error(
			`Lorem source "${name}" is not registered. Did you forget to import sources/index.ts?`,
		);
	}
	const promise = loader();
	cache.set(name, promise);
	return promise;
}

/** True when the source has been registered. */
export function hasSource(name: LoremSourceName): boolean {
	return loaders.has(name);
}

/** Returns the list of currently-registered source names. */
export function listSources(): LoremSourceName[] {
	return Array.from(loaders.keys());
}

/** Test-only — clear the loader + cache map. */
export function _resetRegistry(): void {
	loaders.clear();
	cache.clear();
}
